import { renderStatus } from 'BomdisoftComponents/chips/render';
import { attributes, type HtmlValue, html, joinClasses } from 'BomdisoftComponents/html/index';
import { renderTooltip } from 'BomdisoftComponents/tooltip/render';

export type TabOverflowLabels = {
  /** Names the back chevron, for both its tooltip and its aria-label. */
  back: string;
  /** Names the forward chevron. */
  forward: string;
};

export type TabOverflowDirection = keyof TabOverflowLabels;

const renderChevron = (direction: TabOverflowDirection) =>
  html`<svg aria-hidden="true" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2.4" viewBox="0 0 24 24"><path${attributes({ d: direction === 'back' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6' })}></path></svg>`;

const renderNudge = (direction: TabOverflowDirection, labels: TabOverflowLabels, tooltipIdOf: (direction: TabOverflowDirection) => string) =>
  html`<span class="bomdisoft-tab-overflow-slot ${direction === 'back' ? 'bomdisoft-tab-overflow-start' : 'bomdisoft-tab-overflow-end'}">${renderTooltip({
    children: html`<button${attributes({ 'aria-label': labels[direction], class: 'bomdisoft-tab-overflow-nudge', 'data-testid': direction === 'back' ? 'tab-scroll-back' : 'tab-scroll-forward', tabindex: -1, type: 'button' })}>${renderChevron(direction)}</button>`,
    id: tooltipIdOf(direction),
    placement: 'bottom',
    title: labels[direction],
  })}</span>`;

export const renderTabOverflowArrows = ({ labels, tooltipIdOf }: { labels: TabOverflowLabels; tooltipIdOf: (direction: TabOverflowDirection) => string }) => html`${renderNudge('back', labels, tooltipIdOf)}${renderNudge('forward', labels, tooltipIdOf)}`;

export type TabOverflowFrameOptions = {
  children: HtmlValue;
  className?: string;
  isMoreAtEnd?: boolean;
  isMoreAtStart?: boolean;
};

export const renderTabOverflowFrame = ({ children, className, isMoreAtEnd = false, isMoreAtStart = false }: TabOverflowFrameOptions) =>
  html`<div${attributes({ class: joinClasses('bomdisoft-tab-overflow', className), 'data-more-end': isMoreAtEnd ? 'true' : undefined, 'data-more-start': isMoreAtStart ? 'true' : undefined })}>${children}</div>`;

export type HorizontalTabItem = {
  badge?: string;
  id: string | number;
  name: string;
};

export type HorizontalTabOptions = {
  activeIndex?: number;
  baseId: string;
  className?: string;
  listClassName?: string;
  listLabel: string;
  panel?: HtmlValue;
  panelClassName?: string;
  scrollLabels: TabOverflowLabels;
  tabClassName?: string;
  tabs: readonly HorizontalTabItem[];
  testId?: string;
  tooltipIdOf: (direction: TabOverflowDirection) => string;
  variant?: 'scrollable' | 'standard' | 'fullWidth';
};

export const renderHorizontalTab = ({ activeIndex = 0, baseId, className, listClassName, listLabel, panel, panelClassName, scrollLabels, tabClassName, tabs, testId, tooltipIdOf, variant = 'scrollable' }: HorizontalTabOptions) => {
  if (tabs.length < 1) return html``;
  const buttons = tabs.map(
    (tab, index) =>
      html`<button${attributes({ 'aria-controls': `${baseId}-panel`, 'aria-selected': index === activeIndex ? 'true' : 'false', class: joinClasses('bomdisoft-tabs-tab', tabClassName), 'data-testid': testId ? `${testId}-tab-${tab.id}` : undefined, id: `${baseId}-tab-${index}`, role: 'tab', tabindex: index === activeIndex ? 0 : -1, type: 'button' })}>${
        tab.badge === undefined ? tab.name : html`<span class="bomdisoft-tabs-badge-label">${tab.name}<span class="bomdisoft-tabs-badge-tag">${renderStatus({ text: tab.badge, variant: 'info' })}</span></span>`
      }</button>`,
  );
  const list = html`<div${attributes({ 'aria-label': listLabel, class: joinClasses('bomdisoft-tabs-list', 'bomdisoft-tab-overflow-scroller', listClassName), role: 'tablist' })}>${buttons}</div>`;
  return html`<div${attributes({ class: joinClasses('bomdisoft-tabs', className), 'data-testid': testId, 'data-variant': variant })}>${renderTabOverflowFrame({ children: [list, renderTabOverflowArrows({ labels: scrollLabels, tooltipIdOf })] })}<section${attributes({ 'aria-labelledby': `${baseId}-tab-${activeIndex}`, class: joinClasses('bomdisoft-tabs-panel', panelClassName), 'data-testid': testId ? `${testId}-panel` : undefined, id: `${baseId}-panel`, role: 'tabpanel' })}>${panel ?? ''}</section></div>`;
};

export type UnderlineTabItem = {
  label: HtmlValue;
  value: string;
};

export type UnderlineTabsOptions = {
  ariaControls?: string;
  className?: string;
  label: string;
  scrollLabels: TabOverflowLabels;
  tabs: readonly UnderlineTabItem[];
  testIdPrefix: string;
  tooltipIdOf: (direction: TabOverflowDirection) => string;
  value: string;
};

export const renderUnderlineTabs = ({ ariaControls, className, label, scrollLabels, tabs, testIdPrefix, tooltipIdOf, value }: UnderlineTabsOptions) => {
  const buttons = tabs.map(
    tab =>
      html`<button${attributes({ 'aria-controls': ariaControls, 'aria-selected': tab.value === value ? 'true' : 'false', class: 'bomdisoft-underline-tabs-tab', 'data-testid': `${testIdPrefix}-${tab.value}`, role: 'tab', tabindex: tab.value === value ? 0 : -1, type: 'button' })}>${tab.label}</button>`,
  );
  const row = html`<div${attributes({ 'aria-label': label, class: joinClasses('bomdisoft-underline-tabs', 'bomdisoft-tab-overflow-scroller', className), role: 'tablist' })}>${buttons}</div>`;
  return renderTabOverflowFrame({ children: [row, renderTabOverflowArrows({ labels: scrollLabels, tooltipIdOf })] });
};
